import { useEffect, useState } from "react";
import { Search, Loader2, BookOpen, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { cleanPastedScripture, scriptureTitle, buildScriptureText } from "@/lib/scripture";
import api from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "lookup", label: "Look up" },
  { id: "paste", label: "Paste passage" },
];

export function ScriptureDialog({ open, onClose, onInsert }) {
  const [tab, setTab] = useState("lookup");
  const [reference, setReference] = useState("");
  const [version, setVersion] = useState("Douay-Rheims");
  const [text, setText] = useState("");
  const [verses, setVerses] = useState(null);
  const [withNumbers, setWithNumbers] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState("");
  const [newFolder, setNewFolder] = useState("");

  useEffect(() => {
    if (!open) return;
    api
      .get("/saved-folders")
      .then(({ data }) => setFolders(data))
      .catch(() => setFolders([]));
  }, [open]);

  const reset = () => {
    setReference("");
    setVersion("Douay-Rheims");
    setText("");
    setVerses(null);
    setNewFolder("");
    setTab("lookup");
  };

  const handleLookup = async () => {
    if (!reference.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/scripture/lookup", { params: { reference, translation: "dra" } });
      setReference(data.reference);
      setVersion(data.version);
      setVerses(data.verses || []);
      setText(buildScriptureText(data.verses, { withNumbers, reference: data.reference }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed. Paste the passage instead.");
      setTab("paste");
    } finally {
      setLoading(false);
    }
  };

  const toggleNumbers = (checked) => {
    setWithNumbers(checked);
    if (verses?.length) setText(buildScriptureText(verses, { withNumbers: checked, reference }));
  };

  const handleSaveFavourite = async () => {
    if (!text.trim()) {
      toast.error("Add the passage text first");
      return;
    }
    setSaving(true);
    try {
      let targetId = folderId;
      if (!targetId) {
        const name = newFolder.trim() || "Scripture favourites";
        const { data } = await api.post("/saved-folders", { category: "Prayer", name });
        targetId = data.id;
        setFolders((f) => [...f, { ...data, prayers: [] }]);
        setFolderId(data.id);
        setNewFolder("");
      }
      await api.post(`/saved-folders/${targetId}/prayers`, {
        category: "Prayer",
        title: scriptureTitle(reference, version),
        body: text.trim(),
      });
      toast.success("Saved to your folders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the passage");
    } finally {
      setSaving(false);
    }
  };

  const handleInsert = () => {
    if (!text.trim()) {
      toast.error("Add the passage text first");
      return;
    }
    onInsert({ title: scriptureTitle(reference, version), body: text.trim() });
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="scripture-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <BookOpen className="h-4 w-4" /> Add scripture
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 border-b border-[var(--brand-border)] dark:border-[var(--brand-border-dark)]">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-sm -mb-px border-b-2 transition-colors ${
                tab === t.id ? "border-[var(--brand-navy)] dark:border-amber-400 font-semibold" : "border-transparent text-muted-foreground"
              }`}
              data-testid={`scripture-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "lookup" ? (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLookup()}
                placeholder="Luke 3:16  ·  John 1:1-5  ·  Psalm 23"
                data-testid="scripture-reference-input"
              />
              <Button onClick={handleLookup} disabled={loading} data-testid="scripture-search-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                Searches the public-domain Douay-Rheims (1899). For NABRE or RSV-CE, use the Paste passage tab.
              </p>
              <label className="flex items-center gap-2 text-xs shrink-0">
                <Switch checked={withNumbers} onCheckedChange={toggleNumbers} data-testid="scripture-verse-numbers-switch" />
                Verse numbers
              </label>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Reference e.g. Luke 3:16" data-testid="scripture-paste-reference-input" />
              <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="Version e.g. NABRE" data-testid="scripture-paste-version-input" />
            </div>
            <Button variant="outline" size="sm" onClick={() => setText(cleanPastedScripture(text))} disabled={!text.trim()} data-testid="scripture-clean-button">
              Tidy up pasted text
            </Button>
            <p className="text-xs text-muted-foreground">
              Paste from your own Bible or app below, then tidy it up to strip verse numbers and stray line breaks.
            </p>
          </div>
        )}

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          placeholder="The passage text appears here and can be edited freely before adding."
          className="font-display"
          data-testid="scripture-text-textarea"
        />

        <div className="rounded-md border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] p-3 space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Save as a favourite (private to you)</p>
          <div className="flex gap-2">
            <select
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              className="h-9 flex-1 rounded-md border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] bg-transparent px-2 text-sm"
              data-testid="scripture-folder-select"
            >
              <option value="">New folder…</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            {!folderId && (
              <Input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} placeholder="Scripture favourites" className="flex-1" data-testid="scripture-new-folder-input" />
            )}
            <Button variant="outline" onClick={handleSaveFavourite} disabled={saving} data-testid="scripture-save-favourite-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground" data-testid="scripture-title-preview">
          Segment title: <span className="font-medium">{scriptureTitle(reference, version)}</span>
        </p>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="scripture-cancel-button">
            Cancel
          </Button>
          <Button onClick={handleInsert} data-testid="scripture-insert-button">
            Add to session
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
